import { TravelDistanceBetweenPointsFragment } from '@graphql/generated';
import { WALKING_THRESHOLD } from '@shared/utils/distanceInMiles';

interface ExtractedDistances
  extends Readonly<{
    walkingDistance?: TravelDistanceBetweenPointsFragment;
    drivingDistance?: TravelDistanceBetweenPointsFragment;
    geoDistance?: TravelDistanceBetweenPointsFragment;
    distance?: TravelDistanceBetweenPointsFragment;
    displayDistance?: TravelDistanceBetweenPointsFragment;
    isWalkable?: boolean;
  }> {}

/**
 * Extracts specific distances from the `distance` array.
 *
 * @param commonDistance - Array of distance objects with `type` fields
 * @returns An object with optional `walkingDistance`, `drivingDistance`, `geoDistance`, and `distance`
 */
export const extractTypedDistances = (distance: ReadonlyArray<TravelDistanceBetweenPointsFragment> = []): ExtractedDistances => {
  const extractedDistance = distance.reduce<{ -readonly [K in keyof ExtractedDistances]: ExtractedDistances[K] }>((acc, distance) => {
    switch (distance.type) {
      case 'WALKING_DISTANCE':
        acc.walkingDistance = distance;
        break;
      case 'DRIVING_DISTANCE':
        acc.drivingDistance = distance;
        break;
      case 'GEO_DISTANCE':
        acc.geoDistance = distance;
        break;
    }
    return acc;
  }, {});

  // Get distance for each type
  const { walkingDistance, drivingDistance, geoDistance } = extractedDistance;

  // Get distance if any of available distance by priority.
  const finalDistance = walkingDistance || drivingDistance || geoDistance;

  // Check for walking threshold to show walking vs driving distance.
  const isWalkable = walkingDistance && walkingDistance?.mi <= WALKING_THRESHOLD;

  // Display distance - Check if walkable distance then return walkable otherwise driving distance
  const displayDistance = isWalkable ? walkingDistance : drivingDistance;

  return { ...extractedDistance, distance: finalDistance, isWalkable, displayDistance };
};
